import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { formatMoney } from "../../components/cart-redesign/pricing";
import ProfileLayout from "../../components/profile-redesign/ProfileLayout";
import Panel from "../../components/profile-redesign/Panel";
import StatTiles from "../../components/profile-redesign/StatTiles";
import OrdersTable from "../../components/profile-redesign/OrdersTable";
import AccountSummary from "../../components/profile-redesign/AccountSummary";
import WishlistStrip from "../../components/profile-redesign/WishlistStrip";
import StateMessage from "../../components/profile-redesign/StateMessage";
import {
  useProfile,
  useProfileOrders,
} from "../../components/profile-redesign/useProfile";
import { isActive, formatDate } from "../../components/profile-redesign/profileData";

const RECENT_LIMIT = 4;

function ProfileDashboard() {
  const { user, isLoading, isError, signOut } = useProfile();
  const { orders, orderCount, totalSpent, isFetching, isEmpty, isError: ordersFailed } =
    useProfileOrders(user?._id);

  const currency = useSelector((state) => state.currency);
  const { wishlistItems } = useSelector((state) => state.wishlist);

  const formatPrice = (value) => formatMoney(currency, value);

  const activeCount = useMemo(
    () => orders.filter((order) => isActive(order.status)).length,
    [orders]
  );

  const recent = orders.slice(0, RECENT_LIMIT);

  const memberSince = user?.createdAt
    ? formatDate(new Date(user.createdAt))
    : null;

  const tiles = [
    { label: "Total Orders", value: String(orderCount).padStart(2, "0"), tone: "blue" },
    { label: "Active Orders", value: String(activeCount).padStart(2, "0"), tone: "amber" },
    { label: "Saved Items", value: String(wishlistItems.length).padStart(2, "0"), tone: "green" },
    { label: "Total Spent", value: formatPrice(totalSpent), tone: "pink" },
  ];

  const ordersBlock = () => {
    if (isFetching) {
      return <StateMessage tone="loading" title="Loading your orders…" />;
    }
    if (ordersFailed) {
      return (
        <StateMessage
          tone="error"
          title="We couldn't load your orders"
          text="Please refresh the page, or try again in a moment."
        />
      );
    }
    if (isEmpty || !recent.length) {
      return (
        <StateMessage
          title="No orders yet"
          text="Once an order is confirmed it will appear here with its live status."
        >
          <div className="c4prf__actions">
            <Link
              className="c4prf__btn c4prf__btn--primary"
              to={process.env.PUBLIC_URL + "/shop-grid-standard"}
            >
              Start Shopping
            </Link>
          </div>
        </StateMessage>
      );
    }
    return <OrdersTable orders={recent} formatPrice={formatPrice} />;
  };

  return (
    <ProfileLayout
      seoTitle="Profile Dashboard"
      title="Dashboard"
      subtitle="Welcome back — here's where your account stands."
      user={user}
      isLoading={isLoading}
      isError={isError}
      onSignOut={signOut}
      actions={
        <Link
          className="c4prf__btn c4prf__btn--outline"
          to={process.env.PUBLIC_URL + "/profile/edit"}
        >
          Edit Profile
        </Link>
      }
    >
      <StatTiles tiles={tiles} />

      <Panel
        id="c4prf-recent"
        title="Recent Orders"
        subtitle="Your most recent confirmed orders."
        actions={
          <Link
            className="c4prf__link"
            to={process.env.PUBLIC_URL + "/profile/orders"}
          >
            View all
          </Link>
        }
      >
        {ordersBlock()}
      </Panel>

      <div className="c4prf__split">
        <Panel id="c4prf-summary" title="Account Summary">
          <AccountSummary user={user} since={memberSince} />
        </Panel>

        <Panel
          id="c4prf-wishlist"
          title={`My Wishlist (${wishlistItems.length})`}
          actions={
            wishlistItems.length ? (
              <Link
                className="c4prf__link"
                to={process.env.PUBLIC_URL + "/wishlist"}
              >
                View all
              </Link>
            ) : null
          }
        >
          {wishlistItems.length ? (
            <WishlistStrip
              items={wishlistItems.slice(0, 6)}
              formatPrice={formatPrice}
            />
          ) : (
            <StateMessage
              title="Nothing saved yet"
              text="Tap the heart on any product to keep it here for later."
            />
          )}
        </Panel>
      </div>
    </ProfileLayout>
  );
}

export default ProfileDashboard;
